import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Tag,
  Space,
  Input,
  InputNumber,
  Modal,
  Form,
  message,
  Badge,
  Popconfirm,
  Select,
  Alert,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  createDevice,
  generateDevice,
  getDevices,
  updateDevice,
  updateDeviceSettings,
  deleteDevice,
  getDeviceOnlineRate,
} from '../../api/device';
import dayjs from 'dayjs';
import apiError from '../../utils/apiError';
import './index.css';

const { Option } = Select;

const Devices = () => {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'edit'
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [form] = Form.useForm();
  const [onlineRateMap, setOnlineRateMap] = useState({}); // 存储设备在线率
  const [generatedDevice, setGeneratedDevice] = useState(null); // 新设备预生成信息
  const [checkingRate, setCheckingRate] = useState({}); // 存储正在查询在线率的设备
  const [modalErrorMsg, setModalErrorMsg] = useState(''); // Modal内错误提示

  // 获取当前登录用户信息
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const userInfo = localStorage.getItem('user');
    if (userInfo) {
      setCurrentUser(JSON.parse(userInfo));
    }
  }, []);

  // 判断是否为管理员
  const isAdmin = currentUser?.role === 'admin';

  const deviceTypeMap = {
    1: '接收终端',
    2: '广播终端',
    4: '电梯终端',
    5: '音柱',
    6: '救援现场终端',
  };

  // 获取设备列表
  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: pageSize,
      };

      // 明确传递 deviceId 为空，表示不过滤
      // 如果不传这个参数，后端也应该返回所有设备
      if (searchText && searchText.trim()) {
        params.deviceId = searchText.trim();
      }

      const res = await getDevices(params);
      setDevices(res.data.devices || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      // 全局 Alert 已自动显示错误
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchText]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // 打开新增设备模态框
  const handleAdd = () => {
    setModalType('add');
    setSelectedDevice(null);
    setGeneratedDevice(null);
    form.resetFields();
    setModalErrorMsg('');
    setModalVisible(true);
  };

  // 新增模式：选择设备类型后自动生成ID和密码，作为默认值填入表单，可手动修改
  const handleDeviceTypeChange = async (value) => {
    if (!value) return;
    try {
      setGeneratedDevice(null);
      const res = await generateDevice(value);
      setGeneratedDevice(res.data);
      form.setFieldsValue({
        deviceId: res.data.deviceId,
        password: res.data.password,
      });
    } catch (error) {
      setModalErrorMsg(error.message || '生成设备信息失败');
      apiError.clear();
    }
  };

  // 新增模式：重新生成设备ID和密码，覆盖当前填写的内容
  const handleRegenerate = async () => {
    const deviceType = form.getFieldValue('deviceType');
    if (!deviceType) {
      message.warning('请先选择设备类型');
      return;
    }
    try {
      const res = await generateDevice(deviceType);
      setGeneratedDevice(res.data);
      form.setFieldsValue({
        deviceId: res.data.deviceId,
        password: res.data.password,
      });
    } catch (error) {
      setModalErrorMsg(error.message || '生成设备信息失败');
      apiError.clear();
    }
  };

  // 打开编辑设备模态框
  const handleEdit = (record) => {
    setModalType('edit');
    setSelectedDevice(record);
    setModalErrorMsg('');
    const fields = {
      deviceId: record.deviceId,
      deviceType: record.deviceType,
      serialNumber: record.serialNumber,
      address: record.address,
      lng: record.lng,
      lat: record.lat,
      dayMagnitude: record.dayMagnitude,
      dayIntensity: record.dayIntensity,
      nightMagnitude: record.nightMagnitude,
      nightIntensity: record.nightIntensity,
    };
    if (isAdmin) {
      fields.status = record.status;
      fields.managerHttp = record.managerHttp;
      fields.password = record.password;
    }
    form.setFieldsValue(fields);
    setModalVisible(true);
  };

  // 提交表单（新增或编辑）
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (modalType === 'add') {
        if (!generatedDevice) {
          message.warning('请先选择设备类型');
          return;
        }
        // 设备ID和密码允许手动修改，以表单中最终填写的值为准
        const payload = {
          deviceId: values.deviceId.trim(),
          deviceType: generatedDevice.deviceType,
          password: values.password,
        };
        await createDevice(payload);
        message.success('设备创建成功\n设备ID：' + payload.deviceId + '\n密码：' + payload.password);
        setModalVisible(false);
        setGeneratedDevice(null);
        form.resetFields();
        fetchDevices();
        return;
      }

      // 调用更新接口（管理员走全量更新，普通用户走设置更新）
      const { deviceId, ...updateData } = values;
      const res = isAdmin
        ? await updateDevice(selectedDevice.deviceId, updateData)
        : await updateDeviceSettings(selectedDevice.deviceId, updateData);
      message.success(res?.data?.message || '修改成功');

      setModalVisible(false);
      form.resetFields();
      fetchDevices();
    } catch (error) {
      console.error('操作失败', error);
      setModalErrorMsg((modalType === 'add' ? '创建失败：' : '修改失败：') + (error.message || '未知错误'));
      apiError.clear();
    }
  };

  // 删除设备
  const handleDelete = async (deviceId) => {
    try {
      await deleteDevice(deviceId);
      message.success('设备删除成功');
      fetchDevices();
    } catch (error) {
      // 全局 Alert 已自动显示错误
    }
  };

  // 查询单个设备的在线率（最近24小时）
  const handleCheckOnlineRate = async (deviceId) => {
    setCheckingRate(prev => ({ ...prev, [deviceId]: true }));
    try {
      const res = await getDeviceOnlineRate(deviceId, {
        interval: 1,
        duration: 24
      });
      const rateData = res.data;
      // 使用 mainDeliver 的在线率
      const mainDeliverRate = rateData.servers?.mainDeliver?.onlineRate || 0;
      setOnlineRateMap(prev => ({
        ...prev,
        [deviceId]: {
          rate: mainDeliverRate,
          lastChecked: new Date().toISOString(),
        }
      }));
    } catch (error) {
      // 全局 Alert 已自动显示错误
    } finally {
      setCheckingRate(prev => ({ ...prev, [deviceId]: false }));
    }
  };

  const columns = [
    {
      title: '设备ID',
      dataIndex: 'deviceId',
      key: 'deviceId',
      width: 120,
    },
    {
      title: '设备类型',
      dataIndex: 'deviceType',
      key: 'type',
      width: 80,
      render: (type) => (
        <Tag color={type === 1 ? 'blue' : type === 2 ? 'green' : 'orange'}>
          {deviceTypeMap[type]}
        </Tag>
      ),
    },
    {
      title: '序列号',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 160,
    },
    {
      title: '安装位置',
      key: 'location',
      width: 260,
      render: (_, record) => (
        <div>
          <div style={{ color: '#333' }}>{record.address || '-'}</div>
          {record.lng != null && record.lat != null && (
            <div style={{ color: '#999', fontSize: 12 }}>
              {record.lng.toFixed(4)}, {record.lat.toFixed(4)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '在线状态',
      key: 'onlineStatus',
      width: 90,
      render: (_, record) => {
        const online = record.mainDeliverOnline || record.auxiDeliverOnline || record.managerOnline;
        return (
          <Badge
            status={online ? 'success' : 'default'}
            text={online ? '在线' : '离线'}
          />
        );
      },
    },
    {
      title: '24小时在线率',
      key: 'onlineRate',
      width: 160,
      render: (_, record) => {
        const rateInfo = onlineRateMap[record.deviceId];
        if (!rateInfo) {
          return (
            <Button
              size="small"
              loading={checkingRate[record.deviceId]}
              onClick={() => handleCheckOnlineRate(record.deviceId)}
            >
              查询在线率
            </Button>
          );
        }
        return (
          <Space>
            <Tag color={rateInfo.rate >= 90 ? 'success' : rateInfo.rate >= 70 ? 'warning' : 'error'}>
              {rateInfo.rate.toFixed(1)}%
            </Tag>
            <Button
              size="small"
              type="link"
              loading={checkingRate[record.deviceId]}
              onClick={() => handleCheckOnlineRate(record.deviceId)}
            >
              刷新
            </Button>
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      render: (status) => (
        <Badge
          status={status === 1 ? 'success' : 'error'}
          text={status === 1 ? '启用' : '禁用'}
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {isAdmin && (
            <Popconfirm
              title="确定要删除这个设备吗？"
              description="删除后设备将无法连接到服务器"
              onConfirm={() => handleDelete(record.deviceId)}
              okText="确定"
              cancelText="取消"
            >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                >
                  删除
                </Button>
              </Popconfirm>
            )}
        </Space>
      ),
    },
  ];

  return (
    <div className="devices-container">
      <h2 className="page-title">设备管理</h2>

      <Card>
        <div className="table-toolbar">
          <Space>
            <Input
              placeholder="搜索设备ID（回车搜索）"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => {
                setPage(1); // 搜索时重置到第一页
                fetchDevices();
              }}
              style={{ width: 250 }}
              allowClear
              onClear={() => {
                setSearchText('');
                setPage(1);
              }}
            />
            <Button
              icon={<SearchOutlined />}
              onClick={() => {
                setPage(1); // 搜索时重置到第一页
                fetchDevices();
              }}
            >
              搜索
            </Button>
          </Space>
          <Space>
            {isAdmin && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                新增设备
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchDevices}
            >
              刷新
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={devices}
          rowKey="deviceId"
          loading={loading}
          scroll={{ x: 1300 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </Card>

      {/* 新增/编辑设备模态框 */}
      <Modal
        title={modalType === 'add' ? '新增设备' : '编辑设备'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setModalErrorMsg('');
        }}
        width={800}
      >
        {/* Modal内错误提示 */}
        <div style={{ minHeight: 42, marginBottom: 16 }}>
          {modalErrorMsg && (
            <Alert
              message={modalErrorMsg}
              type="error"
              showIcon
              closable
              onClose={() => setModalErrorMsg('')}
            />
          )}
        </div>
        <Form form={form} layout="vertical" size="middle">
          <Form.Item
            name="deviceType"
            label="设备类型"
            rules={[{ required: true, message: '请选择设备类型' }]}
          >
            <Select
              placeholder="请选择设备类型"
              disabled={modalType === 'edit'}
              onChange={modalType === 'add' ? handleDeviceTypeChange : undefined}
            >
              <Option value={1}>接收终端</Option>
              <Option value={2}>广播终端</Option>
              <Option value={4}>电梯终端</Option>
              <Option value={5}>音柱</Option>
              <Option value={6}>救援现场终端</Option>
            </Select>
          </Form.Item>

          {modalType === 'add' && generatedDevice && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="deviceId"
                  label="设备ID"
                  tooltip="设备连接服务器时使用的账号，可手动修改"
                  rules={[
                    { required: true, message: '请输入设备ID' },
                    { pattern: /^\S+$/, message: '设备ID不能包含空格' },
                    { max: 64, message: '设备ID不能超过64个字符' },
                  ]}
                >
                  <Input
                    placeholder="设备连接服务器时使用的账号"
                    addonAfter={
                      <ReloadOutlined
                        title="重新生成"
                        style={{ cursor: 'pointer' }}
                        onClick={handleRegenerate}
                      />
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="password"
                  label="设备密码"
                  tooltip="设备连接服务器时使用的密码，可手动修改"
                  rules={[
                    { required: true, message: '请输入设备密码' },
                    { min: 6, message: '密码至少6位' },
                  ]}
                >
                  <Input.Password placeholder="设备连接服务器时使用的密码（至少6位）" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {modalType === 'edit' && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="deviceId" label="设备ID">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="serialNumber"
                    label="序列号"
                    tooltip="设备连接后会自动更新序列号"
                  >
                    <Input
                      readOnly
                      addonAfter={
                        isAdmin ? (
                          <Popconfirm
                            title="确认清空序列号"
                            description="清空后设备首次连接时自动绑定新序列号。"
                            onConfirm={() => form.setFieldValue('serialNumber', '')}
                            okText="确定"
                            cancelText="取消"
                          >
                            <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} />
                          </Popconfirm>
                        ) : undefined
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              {isAdmin && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="password"
                      label="设备密码"
                      extra="不修改则保持原样"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value && value.length < 6) {
                              return Promise.reject(new Error('密码至少6位'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input.Password placeholder="新密码（至少6位）" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="status"
                      label="设备状态"
                      rules={[{ required: true, message: '请选择设备状态' }]}
                    >
                      <Select>
                        <Option value={1}>启用</Option>
                        <Option value={2}>禁用</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <div className="section-divider">位置</div>

              <Row gutter={16}>
                <Col span={isAdmin && selectedDevice?.deviceModel !== '1.0' ? 12 : 24}>
                  <Form.Item name="address" label="安装位置">
                    <Input placeholder="设备详细安装地址" />
                  </Form.Item>
                </Col>
                {isAdmin && selectedDevice?.deviceModel !== '1.0' && (
                  <Col span={12}>
                    <Form.Item name="managerHttp" label="管理服务器(HTTP)">
                      <Input placeholder="例如 http://192.168.1.100:8080" />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="lng"
                    label="经度"
                    rules={[{ type: 'number', min: -180, max: 180, message: '经度范围 -180~180' }]}
                  >
                    <InputNumber style={{ width: '100%' }} placeholder="例如 104.06" step={0.0001} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="lat"
                    label="纬度"
                    rules={[{ type: 'number', min: -90, max: 90, message: '纬度范围 -90~90' }]}
                  >
                    <InputNumber style={{ width: '100%' }} placeholder="例如 30.67" step={0.0001} />
                  </Form.Item>
                </Col>
              </Row>

              <div className="section-divider">震级和烈度阈值</div>

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item name="dayMagnitude" label="白天震级">
                    <InputNumber style={{ width: '100%' }} min={0} max={10} step={0.1} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="dayIntensity" label="白天烈度">
                    <InputNumber style={{ width: '100%' }} min={0} max={12} step={0.5} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="nightMagnitude" label="夜晚震级">
                    <InputNumber style={{ width: '100%' }} min={0} max={10} step={0.1} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="nightIntensity" label="夜晚烈度">
                    <InputNumber style={{ width: '100%' }} min={0} max={12} step={0.5} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Devices;
